const {
  fetchNotes,
  fetchNoteTags,
  fetchNote,
  createNote,
  updateNote,
  deleteNote,
} = require("../../sync/engine");
const { SYSTEM_ACTIVITY_TAGS, isSystemActivityTag } = require("../../helpers/todoActivityLog.js");

const PAGE_SIZE = 20;

const state = {
  items: [],
  tags: [],
  nextCursor: null,
  activeTag: null,
  activeDay: null, // YYYY-MM-DD local, or null
  loading: false,
  error: "",
};

const getters = {
  notesItems(state) {
    return state.items;
  },
  notesTags(state) {
    return state.tags;
  },
  notesNextCursor(state) {
    return state.nextCursor;
  },
  notesActiveTag(state) {
    return state.activeTag;
  },
  notesActiveDay(state) {
    return state.activeDay;
  },
  notesLoading(state) {
    return state.loading;
  },
  notesError(state) {
    return state.error;
  },
};

function dayBounds(day) {
  if (!day) return { from: undefined, to: undefined };
  // Local calendar day → ISO range for server created_at filter
  const start = new Date(`${day}T00:00:00`);
  if (Number.isNaN(start.getTime())) return { from: undefined, to: undefined };
  const end = new Date(start.getTime());
  end.setDate(end.getDate() + 1);
  return { from: start.toISOString(), to: end.toISOString() };
}

function noteMatchesFilters(note, activeTag, activeDay) {
  const tags = note && Array.isArray(note.tags) ? note.tags : [];
  if (activeTag) {
    if (!tags.includes(activeTag)) return false;
  } else if (tags.some((t) => isSystemActivityTag(t))) {
    // 「全部」hides system activity tags (same rule as excludeTags on the server).
    return false;
  }
  if (activeDay) {
    const { from, to } = dayBounds(activeDay);
    const created = note && note.createdAt;
    if (!created || !from || !to) return false;
    if (created < from || created >= to) return false;
  }
  return true;
}

function listQuery(state) {
  const { from, to } = dayBounds(state.activeDay);
  const query = {
    limit: PAGE_SIZE,
    tag: state.activeTag || undefined,
    from,
    to,
  };
  if (!state.activeTag) {
    query.excludeTags = SYSTEM_ACTIVITY_TAGS;
  }
  return query;
}

const mutations = {
  setNotesLoading(state, loading) {
    state.loading = loading;
  },
  setNotesError(state, error) {
    state.error = error || "";
  },
  setNotesActiveTag(state, tag) {
    state.activeTag = tag || null;
  },
  setNotesActiveDay(state, day) {
    state.activeDay = day || null;
  },
  resetNotesList(state) {
    state.items = [];
    state.nextCursor = null;
  },
  setNotesPage(state, { notes, nextCursor }) {
    state.items = Array.isArray(notes) ? notes : [];
    state.nextCursor = nextCursor || null;
  },
  appendNotesPage(state, { notes, nextCursor }) {
    if (Array.isArray(notes) && notes.length) {
      state.items = state.items.concat(notes);
    }
    state.nextCursor = nextCursor || null;
  },
  setNotesTags(state, tags) {
    state.tags = Array.isArray(tags) ? tags : [];
  },
  unshiftNote(state, note) {
    if (!note) return;
    state.items.unshift(note);
  },
  replaceNote(state, note) {
    if (!note || !note.id) return;
    const idx = state.items.findIndex((n) => n.id === note.id);
    if (idx >= 0) {
      state.items.splice(idx, 1, note);
    } else {
      state.items.unshift(note);
    }
  },
  removeNoteById(state, id) {
    const idx = state.items.findIndex((n) => n.id === id);
    if (idx >= 0) {
      state.items.splice(idx, 1);
    }
  },
};

async function refreshTags({ commit }) {
  const tagsJson = await fetchNoteTags();
  commit("setNotesTags", tagsJson && tagsJson.tags);
}

const actions = {
  async notesLoadInitial({ commit, state }, payload = {}) {
    const tag = payload && Object.prototype.hasOwnProperty.call(payload, "tag")
      ? payload.tag || null
      : state.activeTag;
    const day = payload && Object.prototype.hasOwnProperty.call(payload, "day")
      ? payload.day || null
      : state.activeDay;
    commit("setNotesError", "");
    commit("setNotesActiveTag", tag);
    commit("setNotesActiveDay", day);
    commit("resetNotesList");
    commit("setNotesLoading", true);
    try {
      const listJson = await fetchNotes(listQuery({ activeTag: tag, activeDay: day }));
      commit("setNotesPage", {
        notes: listJson && listJson.notes,
        nextCursor: listJson && listJson.nextCursor,
      });
      try {
        const tagsJson = await fetchNoteTags();
        commit("setNotesTags", tagsJson && tagsJson.tags);
      } catch (_) {
        commit("setNotesTags", []);
      }
    } catch (err) {
      commit("setNotesError", (err && err.message) || "加载笔记失败");
      commit("setNotesPage", { notes: [], nextCursor: null });
      throw err;
    } finally {
      commit("setNotesLoading", false);
    }
  },

  async notesLoadMore({ commit, state }) {
    if (!state.nextCursor || state.loading) return;
    commit("setNotesError", "");
    commit("setNotesLoading", true);
    try {
      const listJson = await fetchNotes({
        ...listQuery(state),
        cursor: state.nextCursor,
      });
      commit("appendNotesPage", {
        notes: listJson && listJson.notes,
        nextCursor: listJson && listJson.nextCursor,
      });
    } catch (err) {
      commit("setNotesError", (err && err.message) || "加载笔记失败");
      throw err;
    } finally {
      commit("setNotesLoading", false);
    }
  },

  async notesSetTagFilter({ dispatch, state }, tag) {
    return dispatch("notesLoadInitial", { tag: tag || null, day: state.activeDay });
  },

  async notesSetDayFilter({ dispatch, state }, day) {
    return dispatch("notesLoadInitial", { tag: state.activeTag, day: day || null });
  },

  async notesLoadOne({ commit }, { id }) {
    if (!id) throw new Error("缺少笔记 id");
    commit("setNotesError", "");
    try {
      const note = await fetchNote(id);
      commit("replaceNote", note);
      return note;
    } catch (err) {
      commit("setNotesError", (err && err.message) || "加载笔记失败");
      throw err;
    }
  },

  async notesCreate({ commit, state }, { body }) {
    commit("setNotesError", "");
    try {
      const note = await createNote({ body });
      if (noteMatchesFilters(note, state.activeTag, state.activeDay)) {
        commit("unshiftNote", note);
      }
      await refreshTags({ commit });
      return note;
    } catch (err) {
      commit("setNotesError", (err && err.message) || "创建笔记失败");
      throw err;
    }
  },

  async notesUpdate({ commit }, { id, body, baseRevision }) {
    commit("setNotesError", "");
    try {
      const note = await updateNote(id, { body, baseRevision });
      commit("replaceNote", note);
      await refreshTags({ commit });
      return note;
    } catch (err) {
      if (err && err.status === 409) {
        commit("setNotesError", (err && err.message) || "更新冲突");
        if (err.note) {
          commit("replaceNote", err.note);
        }
      } else {
        commit("setNotesError", (err && err.message) || "更新笔记失败");
      }
      throw err;
    }
  },

  async notesRemove({ commit }, { id }) {
    commit("setNotesError", "");
    try {
      await deleteNote(id);
      commit("removeNoteById", id);
      await refreshTags({ commit });
    } catch (err) {
      commit("setNotesError", (err && err.message) || "删除笔记失败");
      throw err;
    }
  },
};

export default {
  namespaced: false,
  state,
  getters,
  actions,
  mutations,
};
